import { useState, type ChangeEvent } from "react";
import { Card, CardContent, ConfirmDialog, Input, Label } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import type { CalendarEvent } from "@tellescope/types-models";
import type { AppointmentListBlock as AppointmentListBlockType, AppointmentCategory } from "../../../../shared/schema";
import { MobileList } from "../../MobileList";
import { useCalendarEvents } from "../../../hooks/useCalendarEvents";
import { useAppointmentsFilter } from "../../../hooks/useItemFilter";
import { useCancelCalendarEvent } from "../../../hooks/useCancelCalendarEvent";
import { useJoinVideoCall } from "../../../hooks/useJoinVideoCall";
import { AppointmentDetailsDialog } from "../AppointmentDetailsDialog";
import { AppointmentCard } from "./AppointmentCard";
import { AppointmentListSkeleton } from "./AppointmentListSkeleton";
import { CategorySelector } from "./CategorySelector";

interface AppointmentListBlockProps {
  block: AppointmentListBlockType;
}

export const AppointmentListBlock = ({ block }: AppointmentListBlockProps) => {
  const { t } = useTranslation("events");
  const categories = block.props.categories ?? ["all"];
  const { itemFilter, allowCancel, rescheduleUrl, rescheduleUrlOverrides, hideVideoAfterMinutes } = block.props;
  const showCategorySelector = categories.length > 1;

  // Initialize with the first category in the list
  const [selectedCategory, setSelectedCategory] = useState<AppointmentCategory>(categories[0] ?? "all");

  // State for cancel confirmation dialog
  const [eventToCancel, setEventToCancel] = useState<CalendarEvent | null>(null);
  const [cancelReason, setCancelReason] = useState("");

  // State for details dialog
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);

  // Cancel mutation
  const { cancelEventAsync, isPending: isCancelling } = useCancelCalendarEvent();

  // Video calling for Twilio events
  const { joinVideoCall: handleJoinVideoCall } = useJoinVideoCall();

  const {
    events: rawEvents,
    isLoading: eventsLoading,
    error,
  } = useCalendarEvents({
    category: selectedCategory,
  });

  // Apply item filter if configured
  const { filteredItems: events, isLoading: filterLoading } = useAppointmentsFilter({
    items: rawEvents,
    itemFilter,
  });

  // Filter out events hidden from portal
  const visibleEvents = events.filter((e) => !e.hiddenFromPortal);

  const isLoading = eventsLoading || filterLoading;

  const getModalTitle = () => {
    switch (selectedCategory) {
      case "upcoming":
        return t("Upcoming Appointments");
      case "past":
        return t("Past Appointments");
      case "all":
      default:
        return t("All Appointments");
    }
  };

  const getDefaultEmptyMessage = () => {
    switch (selectedCategory) {
      case "upcoming":
        return t("No upcoming appointments");
      case "past":
        return t("No past appointments");
      case "all":
      default:
        return t("No appointments");
    }
  };

  const handleCancelClick = (event: CalendarEvent) => {
    setCancelReason("");
    setEventToCancel(event);
  };

  const handleConfirmCancel = async () => {
    if (!eventToCancel) return;
    await cancelEventAsync({
      eventId: eventToCancel.id,
      cancelReason: cancelReason || undefined,
    });
    setCancelReason("");
    setEventToCancel(null);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {showCategorySelector && (
          <CategorySelector
            categories={categories}
            selectedCategory={selectedCategory}
            onSelectCategory={setSelectedCategory}
          />
        )}
        <AppointmentListSkeleton count={block.props.maxItems ?? 3} />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="p-4 text-center text-destructive">{t("Failed to load appointments")}</CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {showCategorySelector && (
        <CategorySelector
          categories={categories}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
        />
      )}
      <MobileList
        items={visibleEvents}
        renderItem={(event) => (
          <AppointmentCard
            event={event}
            allowCancel={allowCancel}
            rescheduleUrl={rescheduleUrlOverrides?.[event.title] ?? rescheduleUrl}
            hideVideoAfterMinutes={hideVideoAfterMinutes}
            onCancelClick={handleCancelClick}
            onJoinVideoCall={handleJoinVideoCall}
            onViewDetails={setSelectedEvent}
          />
        )}
        title={getModalTitle()}
        maxItems={block.props.maxItems ?? 3}
        emptyMessage={block.props.emptyMessage ?? getDefaultEmptyMessage()}
      />

      {/* Details dialog */}
      <AppointmentDetailsDialog
        event={selectedEvent}
        open={!!selectedEvent}
        onOpenChange={(open) => !open && setSelectedEvent(null)}
        allowCancel={allowCancel ?? false}
        rescheduleUrl={selectedEvent ? rescheduleUrlOverrides?.[selectedEvent.title] ?? rescheduleUrl : rescheduleUrl}
        hideVideoAfterMinutes={hideVideoAfterMinutes}
      />

      {/* Cancel confirmation dialog */}
      <ConfirmDialog
        open={!!eventToCancel}
        onOpenChange={(open) => {
          if (!open) {
            setCancelReason("");
            setEventToCancel(null);
          }
        }}
        variant="destructive"
        title={t("Cancel Appointment")}
        description={
          eventToCancel
            ? t('Are you sure you want to cancel your appointment "{{title}}" scheduled for {{date}}?', {
                title: eventToCancel.displayTitle ?? eventToCancel.title,
                date: new Date(eventToCancel.startTimeInMS).toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  hour: "numeric",
                  minute: "2-digit",
                }),
              })
            : ""
        }
        confirmText={t("Yes, cancel appointment")}
        cancelText={t("Keep appointment")}
        onConfirm={handleConfirmCancel}
        loading={isCancelling}
        confirmDisabled={!!eventToCancel?.requirePortalCancelReason && !cancelReason.trim()}
        customContent={
          <div className="space-y-1">
            <Label htmlFor="cancelReason" required={eventToCancel?.requirePortalCancelReason === true}>
              {t("Reason for cancellation")}
            </Label>
            <Input
              id="cancelReason"
              value={cancelReason}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setCancelReason(e.target.value)}
              placeholder={t("Please provide a reason")}
            />
          </div>
        }
      />
    </div>
  );
};
